<template>
  <tr>
    <td align="left" :style="rootStyles">
      <table
        border="0"
        cellpadding="0"
        cellspacing="0"
        role="presentation"
        width="100%"
      >
        <tr>
          <td align="left" style="padding: 0px; word-break: break-word">
            <div :style="contentStyles">
              <template v-if="!isBuilder">
                <div v-html="contentModel.content" />
              </template>

              <template v-if="isBuilder">
                <ak-text-editor
                  v-model="contentModel.content"
                  :config="titleConfig"
                  type="inline"
                />
              </template>
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import TextEditor from '@akerolabs/akero-vue/src/components/textEditor/TextEditor'
import config from '../../utils/config'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-title></ak-email-template-title>
   */
  name: 'AkEmailTemplateTitle',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [TextEditor.name]: TextEditor,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        content: config.defaultTitle,
        type: 'title',
        style: {
          color: '#353535',
          size: 20,
          align: 'left',
        },
      }),
    },

    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    isBuilder: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the content item is the last in its column
     */
    isLast: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Has flush spacing on the block
     */
    isFlush: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Content index
     */
    contentIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description The width of the column
     */
    width: {
      type: Number,
      default: null,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const contentModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const titleConfig = ref({
      extraPlugins:
        'sharedspace,colorbutton,colordialog,font,justify,akvariables',
      toolbar: [
        ['FontSize', 'Bold', 'Italic', 'Underline'],
        ['TextColor', 'BGColor'],
        ['Link', 'Unlink'],
        ['JustifyLeft', 'JustifyCenter', 'JustifyRight'],
        ['RemoveFormat'],
        ['AKVariables'],
      ],
      uiColor: '#ffffff',
      sharedSpaces: {
        top: 'builder-toolbar',
      },
    })

    const rootStyles = computed(() => {
      return {
        fontSize: '0px',
        padding: '0',
        wordBreak: 'break-word',
        paddingTop:
          !props.isBuilder && props.contentIndex === 0 && !props.isFlush
            ? '10px'
            : '0',
        paddingBottom: props.isBuilder || props.isLast ? '0' : '10px',
      }
    })

    const contentStyles = computed(() => {
      return {
        msoLineHeight: 'exactly',
        lineHeight: '1.15',
        fontFamily: `${props.settings.headingsFont},sans-serif`,
        textAlign: contentModel.value.style.align,
        fontSize: `${contentModel.value.style.size}px`,
        color: contentModel.value.style.color,
      }
    })

    return {
      titleConfig,
      rootStyles,
      contentStyles,
      contentModel,
    }
  },
})
</script>
